import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CostCentre } from '@/types';

const STORAGE_KEY = 'scheduler-cost-centre-selector-state';
const MAX_RECENT_COST_CENTRES = 10;

interface CostCentreSelectorState {
  favourites: string[];
  recentlyUsed: Array<{ id: string; timestamp: number }>;
}

export function useCostCentreSelector(costCentres: CostCentre[], projectId?: string) {
  const [state, setState] = useState<CostCentreSelectorState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        };
      }
    } catch (error) {
      console.error('Failed to load cost centre selector state:', error);
    }
    return { favourites: [], recentlyUsed: [] };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save cost centre selector state:', error);
    }
  }, [state]);

  const toggleFavourite = useCallback((costCentreId: string) => {
    setState((prev) => {
      const isFav = prev.favourites.includes(costCentreId);
      return {
        ...prev,
        favourites: isFav
          ? prev.favourites.filter((id) => String(id) !== String(costCentreId))
          : [...prev.favourites, costCentreId],
      };
    });
  }, []);

  const recordCostCentreAccess = useCallback((costCentreId: string) => {
    setState((prev) => {
      const filtered = prev.recentlyUsed.filter((item) => String(item.id) !== String(costCentreId));
      const updated = [
        { id: costCentreId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_COST_CENTRES);
      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, []);

  const enrichedCostCentres = useMemo(() => {
    return costCentres.map((costCentre) => ({
      ...costCentre,
      isFavourite: state.favourites.includes(String(costCentre.id)),
            lastAccessedDate: state.recentlyUsed.find((item) => String(item.id) === String(costCentre.id))
              ? new Date(state.recentlyUsed.find((item) => String(item.id) === String(costCentre.id))!.timestamp)
        : undefined,
    }));
  }, [costCentres, state.favourites, state.recentlyUsed]);

  const favouriteCostCentres = useMemo(() => {
    // Only show favorites that belong to the current project (i.e., are in the costCentres array)
    return enrichedCostCentres
      .filter((cc) => cc.isFavourite)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [enrichedCostCentres]);

  const recentCostCentres = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);
    
    // Get cost centres that have been accessed AND belong to the current project
    const accessedCostCentres = enrichedCostCentres
      .filter((cc) => recentIds.includes(String(cc.id)))
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      });
    
    // If we have fewer than 5 accessed cost centres, fill with other cost centres from current project (alphabetically)
    if (accessedCostCentres.length < 5) {
      const accessedIds = new Set(accessedCostCentres.map(cc => String(cc.id)));
      const otherCostCentres = enrichedCostCentres
        .filter(cc => !accessedIds.has(String(cc.id)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      return [...accessedCostCentres, ...otherCostCentres].slice(0, 5);
    }
    
    return accessedCostCentres.slice(0, 5); // Always show exactly 5 recent cost centres
  }, [enrichedCostCentres, state.recentlyUsed]);

  return {
    enrichedCostCentres,
    favouriteCostCentres,
    recentCostCentres,
    toggleFavourite,
    recordCostCentreAccess,
    isFavourite: (costCentreId: string) => state.favourites.includes(costCentreId),
  };
}
